import { ForwardedRef, forwardRef, useCallback } from 'react'
import {
  DrawController,
  DrawControllerProps,
  DrawControllerRef,
  TransientDrawState,
} from '@modugen/ui-toolkit'
import { useControlStore } from '@editorStores'

/**
 * Drop-in wrapper around the ui-toolkit `DrawController`. Kept as a single
 * import point so callers stay stable if a rebuilt controller is wired in later.
 *
 * `snapAngle` comes from the control store (default 90°) unless a caller
 * passes it explicitly. Do not remount on raster changes: the toolkit's
 * unmount cleanup calls `onDrawEnd` and would commit an in-progress draw.
 * The mousemove listener closes over `snapAngle` and re-attaches when
 * `onMouseMove` changes, so the raster is folded into that callback identity.
 */
const DrawControllerWrapper = forwardRef(function DrawControllerWrapper(
  { onMouseMove, snapAngle: snapAngleProp, ...props }: DrawControllerProps,
  ref: ForwardedRef<DrawControllerRef>,
) {
  const snapAngle = useControlStore(state => state.snapAngle)
  const resolvedSnapAngle = snapAngleProp ?? snapAngle

  const handleMouseMove = useCallback(
    (state: TransientDrawState) => {
      onMouseMove?.(state)
    },
    [onMouseMove, resolvedSnapAngle],
  )

  return (
    <DrawController
      ref={ref}
      {...props}
      snapAngle={resolvedSnapAngle}
      onMouseMove={onMouseMove ? handleMouseMove : undefined}
    />
  )
})

export default DrawControllerWrapper
